import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import DataModal from "./DataModal";
import HostTooltipButton from "./HostTooltipButton";
import interactiveRegistry from "~/Registry/Interactive/InteractiveRegistry";
import { useChartsStore } from "~/store/chartStore";
import InteractiveEditModal from "./InteractiveEditModal";
import { TOOL_ICON_SIZE, BTN_HEIGHT } from "../Workspace/sidebarConstants";
import getChartHostBody from "~/utils/chartHostBody";

const ActiveObjectsModal = forwardRef(function ActiveObjectsModal(
  // `hideTrigger` + imperative `open()` let the chart context menu reuse this
  // modal without rendering the sidebar trigger button (feature chartContextMenu).
  { interactiveApi, viewMode = "grid", sidebarWidth = 36, hideTrigger = false },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedObjectForEdit, setSelectedObjectForEdit] = useState(null);
  // Janela onde o gráfico alvo vive — resolvida no clique, nunca no render (#375).
  const [editModalHostBody, setEditModalHostBody] = useState(null);

  // Imperative open — used by the chart context menu ("Objetos Ativos…")
  useImperativeHandle(ref, () => ({ open: () => setVisible(true) }), []);

  // Chart store actions
  const deleteInteractiveObject = useChartsStore(
    (state) => state.deleteInteractiveObject,
  );
  const updateInteractiveObjectAppearance = useChartsStore(
    (state) => state.updateInteractiveObjectAppearance,
  );

  // Get chartId and chartSectionId from the interactiveApi
  const chartId = interactiveApi?.chartId;
  const chartSectionId = interactiveApi?.chartSectionId;

  // Get active objects from the interactive API
  const activeObjects = useMemo(() => {
    if (!interactiveApi || !interactiveApi.objectsMap) {
      return [];
    }

    const objectsList = [];
    Object.entries(interactiveApi.objectsMap).forEach(([type, objects]) => {
      // TODO: Re-enable when notification sound issues are resolved
      // Skip stopLine objects (feature temporarily disabled)
      if (type === "stopLine") {
        return;
      }

      if (Array.isArray(objects)) {
        objects.forEach((obj, index) => {
          if (obj) {
            // Try to find the displayName and category from the registry
            let displayName = type;
            let categoryName = "";

            // Search through all categories in the registry
            for (const [, category] of Object.entries(interactiveRegistry)) {
              if (category.interactives && category.interactives[type]) {
                displayName = category.interactives[type].displayName;
                categoryName = category.displayName;
                break;
              }
            }

            objectsList.push({
              id: `${type}_${index}`,
              type: type,
              name: displayName,
              category: categoryName,
              index: index,
              selected: obj.selected || false,
              object: obj,
            });
          }
        });
      }
    });

    return objectsList;
  }, [interactiveApi]);

  // Fluid dimensions
  const dynamicDimensions = useMemo(() => {
    return {
      width: "min(700px, 92vw)",
    };
  }, []);

  // Helper function to convert index to date if possible
  const convertIndexToDate = (index) => {
    const { chartData, displayXAccessor } = interactiveApi || {};

    if (!chartData || !displayXAccessor || !Array.isArray(chartData)) {
      return index; // Return index as-is if we can't convert
    }

    // Ensure index is within bounds
    const actualIndex = Math.round(index);
    if (actualIndex < 0 || actualIndex >= chartData.length) {
      return index;
    }

    try {
      // Get the data item at this index and convert to date
      const dataItem = chartData[actualIndex];
      const date = displayXAccessor(dataItem);

      // Format the date based on type
      if (date instanceof Date) {
        // Check if we need time (for intraday data)
        const hasTime =
          chartData.length > 1 &&
          chartData[1] &&
          chartData[0] &&
          Math.abs(
            displayXAccessor(chartData[1]).getTime() -
              displayXAccessor(chartData[0]).getTime(),
          ) <
            24 * 60 * 60 * 1000;

        if (hasTime) {
          // Intraday data - include time
          return (
            date.toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }) +
            " " +
            date.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })
          );
        } else {
          // Daily data - just date
          return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
      }

      return date;
    } catch (error) {
      console.warn("Error converting index to date:", error);
      return index;
    }
  };

  // Helper function to format coordinates based on object type
  const formatCoordinates = (objectData) => {
    const { type, object } = objectData;

    if (!object) return "-";

    // Helper function to format X coordinate (try to convert index to date if possible)
    const formatXCoordinate = (xValue) => {
      const converted = convertIndexToDate(xValue);
      if (converted !== xValue) {
        // Successfully converted to date
        return converted;
      }
      // Fallback to index
      return typeof xValue === "number" ? Math.round(xValue) : xValue;
    };

    // Helper function to format Y coordinate (price)
    const formatYCoordinate = (yValue) => {
      return typeof yValue === "number" ? yValue.toFixed(2) : yValue;
    };

    try {
      switch (type) {
        case "trendLine":
          // TrendLine uses start and end coordinates
          if (object.start && object.end) {
            const startX = formatXCoordinate(object.start[0]);
            const startY = formatYCoordinate(object.start[1]);
            const endX = formatXCoordinate(object.end[0]);
            const endY = formatYCoordinate(object.end[1]);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          }
          break;

        case "fibRetracement":
          // FibRetracement uses x1, y1, x2, y2 properties
          if (object.y1 !== undefined && object.y2 !== undefined) {
            const startX = formatXCoordinate(object.x1);
            const startY = formatYCoordinate(object.y1);
            const endX = formatXCoordinate(object.x2);
            const endY = formatYCoordinate(object.y2);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          }
          break;

        case "fibProjection":
          // FibProjection uses x1, y1, x2, y2, x3, y3 properties (3 points)
          if (
            object.y1 !== undefined &&
            object.y2 !== undefined &&
            object.y3 !== undefined
          ) {
            const point1X = formatXCoordinate(object.x1);
            const point1Y = formatYCoordinate(object.y1);
            const point2X = formatXCoordinate(object.x2);
            const point2Y = formatYCoordinate(object.y2);
            const point3X = formatXCoordinate(object.x3);
            const point3Y = formatYCoordinate(object.y3);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  P1: ({point1X}, {point1Y})
                </div>
                <div style={{ marginBottom: "4px" }}>
                  P2: ({point2X}, {point2Y})
                </div>
                <div>
                  P3: ({point3X}, {point3Y})
                </div>
              </div>
            );
          }
          break;

        case "equidistantChannel":
          // EquidistantChannel uses startXY, endXY, and dy
          if (
            object.startXY &&
            object.endXY &&
            Array.isArray(object.startXY) &&
            Array.isArray(object.endXY)
          ) {
            const startX = formatXCoordinate(object.startXY[0]);
            const startY = formatYCoordinate(object.startXY[1]);
            const endX = formatXCoordinate(object.endXY[0]);
            const endY = formatYCoordinate(object.endXY[1]);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div
                  style={{
                    marginBottom:
                      object.dy !== undefined && object.dy !== 0 ? "4px" : "0",
                  }}
                >
                  ({endX}, {endY})
                </div>
                {object.dy !== undefined && object.dy !== 0 && (
                  <div>dy: {formatYCoordinate(object.dy)}</div>
                )}
              </div>
            );
          }
          break;

        case "stdDevChannel":
          // StandardDeviationChannel uses start and end arrays
          if (object.start && object.end) {
            const startX = formatXCoordinate(object.start[0]);
            const startY = formatYCoordinate(object.start[1]);
            const endX = formatXCoordinate(object.end[0]);
            const endY = formatYCoordinate(object.end[1]);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          } else if (object.y1 !== undefined && object.y2 !== undefined) {
            const startX = formatXCoordinate(object.x1);
            const startY = formatYCoordinate(object.y1);
            const endX = formatXCoordinate(object.x2);
            const endY = formatYCoordinate(object.y2);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          }
          break;

        case "gannFan":
          // GannFan uses startXY and endXY arrays
          if (
            object.startXY &&
            object.endXY &&
            Array.isArray(object.startXY) &&
            Array.isArray(object.endXY)
          ) {
            const startX = formatXCoordinate(object.startXY[0]);
            const startY = formatYCoordinate(object.startXY[1]);
            const endX = formatXCoordinate(object.endXY[0]);
            const endY = formatYCoordinate(object.endXY[1]);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          }
          break;

        case "interactiveYCoordinate":
          // Y coordinate has a yValue
          if (object.yValue !== undefined) {
            const yFormatted = formatYCoordinate(object.yValue);
            return <div>Y: {yFormatted}</div>;
          }
          // Try alternative property names
          if (object.value !== undefined) {
            const yFormatted = formatYCoordinate(object.value);
            return <div>Y: {yFormatted}</div>;
          }
          if (object.y !== undefined) {
            const yFormatted = formatYCoordinate(object.y);
            return <div>Y: {yFormatted}</div>;
          }
          break;

        case "interactiveText":
          // InteractiveText uses position array [x, y] and has text content
          if (
            object.position &&
            Array.isArray(object.position) &&
            object.position.length >= 2
          ) {
            const posX = formatXCoordinate(object.position[0]);
            const posY = formatYCoordinate(object.position[1]);

            // Extract text from various possible locations
            let text = "Texto não definido";

            // Check appearance.text.text first (nested object)
            if (
              object.appearance &&
              object.appearance.text &&
              typeof object.appearance.text === "object" &&
              object.appearance.text.text
            ) {
              text = object.appearance.text.text;
            }
            // Check appearance.text (simple string)
            else if (
              object.appearance &&
              object.appearance.text &&
              typeof object.appearance.text === "string"
            ) {
              text = object.appearance.text;
            }
            // Check direct text property
            else if (object.text) {
              text = object.text;
            }

            // Ensure it's a string and not "Lorem ipsum..."
            text = String(text);
            if (text === "Lorem ipsum..." || text === "Lorem ipsum") {
              text = "Texto não definido";
            }

            return (
              <div>
                <div style={{ marginBottom: "4px", fontWeight: "bold" }}>
                  &ldquo;{text}&rdquo;
                </div>
                <div>
                  ({posX}, {posY})
                </div>
              </div>
            );
          }
          break;

        case "arrow":
        case "heart":
        case "star":
        case "check":
        case "cross":
        case "rectangle":
        case "ellipse":
          // Shape tools use x, y for center position
          if (object.x !== undefined && object.y !== undefined) {
            const centerX = formatXCoordinate(object.x);
            const centerY = formatYCoordinate(object.y);

            return (
              <div>
                Centro: ({centerX}, {centerY})
              </div>
            );
          }
          break;

        case "segment":
        case "continuous":
        case "ray":
          // Segment, Continuous and Ray use start and end coordinates (similar to trendLine)
          if (
            object.start &&
            object.end &&
            Array.isArray(object.start) &&
            Array.isArray(object.end)
          ) {
            const startX = formatXCoordinate(object.start[0]);
            const startY = formatYCoordinate(object.start[1]);
            const endX = formatXCoordinate(object.end[0]);
            const endY = formatYCoordinate(object.end[1]);

            return (
              <div>
                <div style={{ marginBottom: "4px" }}>
                  ({startX}, {startY})
                </div>
                <div>
                  ({endX}, {endY})
                </div>
              </div>
            );
          }
          break;

        case "verticalLine":
          // Vertical line has an xValue (index position)
          if (object.xValue !== undefined) {
            const xFormatted = formatXCoordinate(object.xValue);
            return <div>X: {xFormatted}</div>;
          }
          break;

        case "horizontalLine":
          // TODO: Re-enable when notification sound issues are resolved
          // case 'stopLine':
          // Horizontal line and Stop line have a yValue (price level)
          if (object.yValue !== undefined) {
            const yFormatted = formatYCoordinate(object.yValue);
            return <div>Y: {yFormatted}</div>;
          }
          break;

        default:
          // Handle other object types or fallback cases
          console.log(`Unknown object type: ${type}`, object);
          break;
      }
    } catch (error) {
      console.warn(
        "Error formatting coordinates for object:",
        objectData,
        error,
      );
    }

    return "-";
  };

  const handleDeleteObject = (objectData) => {
    if (deleteInteractiveObject && chartId !== undefined) {
      deleteInteractiveObject(
        chartId,
        chartSectionId,
        objectData.type,
        objectData.index,
      );
      console.log("Deleted object:", objectData);
    }
  };

  const handleDeleteAllObjects = () => {
    if (!deleteInteractiveObject || chartId === undefined || !interactiveApi)
      return;

    // Group objects by type
    const objectsByType = {};
    activeObjects.forEach((objectData) => {
      if (!objectsByType[objectData.type]) {
        objectsByType[objectData.type] = [];
      }
      objectsByType[objectData.type].push(objectData.index);
    });

    // Delete all objects of each type (delete from highest index to lowest to avoid index shifting)
    Object.entries(objectsByType).forEach(([type, indices]) => {
      // Sort indices in descending order
      const sortedIndices = indices.sort((a, b) => b - a);
      sortedIndices.forEach((index) => {
        deleteInteractiveObject(chartId, chartSectionId, type, index);
      });
    });

    console.log("Deleted all objects");
  };

  const handleEditObject = (objectData) => {
    // Create an interactive object structure similar to InteractiveContextMenu
    const interactive = {
      type: objectData.type,
      chartId: chartSectionId, // This is the chart section ID (1 for main chart)
      index: objectData.index,
      object: objectData.object,
      moreProps: {
        chartConfig: { id: chartSectionId },
      },
    };

    setSelectedObjectForEdit(interactive);
    setEditModalHostBody(getChartHostBody(chartId));
    setEditModalVisible(true);
    console.log("Opening edit modal for object:", objectData);
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
    setSelectedObjectForEdit(null);
  };

  const handleEditModalSave = (interactiveId, updatedAppearance) => {
    console.log("ActiveObjectsModal.handleEditModalSave called:", {
      interactiveId,
      updatedAppearance,
    });

    // Parse the interactive ID to extract components
    const parts = interactiveId.split("_");
    if (parts.length >= 4) {
      const chartIdPart = parts[0];
      const chartSectionIdPart = parts[1];
      const objectType = parts[2];
      const objectIndex = parseInt(parts[3], 10);

      // Update the object appearance using chartStore
      updateInteractiveObjectAppearance(
        chartIdPart,
        chartSectionIdPart,
        objectType,
        objectIndex,
        updatedAppearance,
      );
    }

    setEditModalVisible(false);
    setSelectedObjectForEdit(null);
  };

  // Helper function to extract color from object based on type
  const getObjectColor = (obj, type) => {
    if (!obj) return "#888888"; // Default gray

    // Try to get color from appearance
    if (obj.appearance) {
      // For text objects, use font color
      if (type === "interactiveText" && obj.appearance.font?.color) {
        return obj.appearance.font.color;
      }
      // For other objects, use stroke color
      if (obj.appearance.stroke?.color) {
        return obj.appearance.stroke.color;
      }
      // Fallback to fill color
      if (obj.appearance.fill?.color) {
        return obj.appearance.fill.color;
      }
    }

    // Legacy format: direct properties
    if (obj.stroke && typeof obj.stroke === "string") {
      return obj.stroke;
    }
    if (obj.font?.color) {
      return obj.font.color;
    }

    return "#888888"; // Default gray
  };

  // Template for name column with color indicator
  const nameBodyTemplate = (rowData) => {
    const color = getObjectColor(rowData.object, rowData.type);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <div
          style={{
            width: "12px",
            height: "12px",
            backgroundColor: color,
            borderRadius: "2px",
            flexShrink: 0,
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        />
        <span style={{ fontSize: "12px", fontWeight: "500" }}>
          {rowData.name}
        </span>
      </div>
    );
  };

  // Custom header with delete all button
  const headerTemplate = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
          Objetos Ativos
        </span>
        {activeObjects.length > 0 && (
          <HostTooltipButton
            label="Excluir Todos"
            icon="pi pi-trash"
            className="p-button-sm p-button-danger p-button-outlined"
            onClick={handleDeleteAllObjects}
            tooltip="Excluir todos os objetos do gráfico"
            tooltipOptions={{
              position: "left",
            }}
            style={{ fontSize: "11px", padding: "4px 8px" }}
            data-evo-id="activeobjectsmodal-btn-delete-all"
          />
        )}
      </div>
    );
  };

  // Template for action buttons
  const actionBodyTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <HostTooltipButton
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => handleEditObject(rowData)}
          tooltip="Editar"
          tooltipOptions={{
            position: "top",
          }}
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id="activeobjectsmodal-btn-edit"
        />
        <HostTooltipButton
          icon="pi pi-trash"
          className="p-button-text p-button-danger p-button-sm"
          onClick={() => handleDeleteObject(rowData)}
          tooltip="Excluir"
          tooltipOptions={{
            position: "top",
          }}
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id="activeobjectsmodal-btn-delete"
        />
      </div>
    );
  };

  return (
    <>
      {!hideTrigger && (
        <div style={{ position: "relative", display: "flex", width: "100%" }}>
          <Button
            className="p-button-rounded p-button-text"
            onClick={() => setVisible(true)}
            tooltip={viewMode === "grid" ? "Objetos Ativos" : undefined}
            tooltipOptions={{ position: "right" }}
            style={{
              width: "100%",
              minWidth: "auto",
              height: `${BTN_HEIGHT}px`,
              fontSize: `${TOOL_ICON_SIZE}px`,
              color: "var(--text-color)",
              border: "1px solid transparent",
              borderRadius: "4px",
              padding: viewMode === "grid" ? "1px" : "1px 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: viewMode === "grid" ? "center" : "flex-start",
              gap: viewMode === "list" ? "6px" : "0",
            }}
            data-evo-id="activeobjectsmodal-btn-view-active"
          >
            <i
              className="pi pi-list"
              style={{ fontSize: `${TOOL_ICON_SIZE}px` }}
            />
            {viewMode === "list" && (
              <span
                style={{
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  color: "var(--text-color)",
                  textAlign: "left",
                }}
              >
                Objetos Ativos
              </span>
            )}
          </Button>
          {activeObjects.length > 0 && (
            <span
              onClick={() => setVisible(true)}
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                background: "var(--blue-500)",
                color: "white",
                borderRadius: "50%",
                width: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "9px",
                fontWeight: "bold",
                border: "1px solid var(--surface-0)",
                zIndex: 10,
                minWidth: "16px",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
              data-evo-id="activeobjectsmodal-object-count"
            >
              {activeObjects.length}
            </span>
          )}
        </div>
      )}

      <DataModal
        title={headerTemplate}
        visible={visible}
        onHide={() => setVisible(false)}
        size="large"
        maximizable={false}
        style={dynamicDimensions}
        contentStyle={{ padding: 0, overflow: "hidden" }}
        className="active-items-modal"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            maxHeight: "min(420px, 70vh)",
          }}
        >
          {activeObjects.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--text-color-secondary)",
                fontSize: "0.85rem",
              }}
            >
              Nenhum objeto ativo encontrado no gráfico.
            </div>
          ) : (
            <DataTable
              value={activeObjects}
              scrollable
              scrollHeight="flex"
              size="small"
              stripedRows
              showGridlines
              emptyMessage="Nenhum objeto encontrado"
              style={{ fontSize: "0.8125rem" }}
            >
              <Column
                field="name"
                header="Nome"
                body={nameBodyTemplate}
                sortable
                style={{ width: "30%" }}
              />
              <Column
                field="category"
                header="Categoria"
                sortable
                style={{ width: "18%", fontSize: "11px" }}
              />
              <Column
                header="Parâmetros"
                body={(rowData) => (
                  <span
                    style={{
                      fontSize: "11px",
                      color: "var(--text-color-secondary)",
                      lineHeight: "1.3",
                    }}
                  >
                    {formatCoordinates(rowData)}
                  </span>
                )}
                style={{ width: "32%" }}
              />
              <Column
                header="Ações"
                body={actionBodyTemplate}
                style={{ width: "20%", textAlign: "center" }}
              />
            </DataTable>
          )}
        </div>
      </DataModal>

      {/* Interactive Edit Modal */}
      {editModalVisible && selectedObjectForEdit && (
        <InteractiveEditModal
          chartId={chartId}
          interactive={selectedObjectForEdit}
          visible={editModalVisible}
          onClose={handleEditModalClose}
          onSave={handleEditModalSave}
          hostBody={editModalHostBody}
        />
      )}
    </>
  );
});

ActiveObjectsModal.propTypes = {
  interactiveApi: PropTypes.shape({
    objectsMap: PropTypes.object,
    handleDrawingObjectDoubleClick: PropTypes.func,
    chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  viewMode: PropTypes.oneOf(["grid", "list"]),
  sidebarWidth: PropTypes.number,
  hideTrigger: PropTypes.bool,
};

export default React.memo(ActiveObjectsModal);
